/**
 * Dedicated Forms Admin app at /formsadmin
 */

import { STATES } from "../data/states.js";
import {
  changePassword,
  DEFAULT_PASSWORD,
  DEFAULT_USERNAME,
  ensureDefaultCredentials,
  getCredentialsMeta,
  getLoginLockoutMs,
  isAuthenticated,
  login,
  logout,
  mustChangePassword,
} from "./formsAdminAuth.js";
import {
  clearDefaultPacketIds,
  ENVISION_FORMS_ONLY_POLICY,
  exportFormsAssignmentSnapshot,
  getAllFormsForState,
  getAssignmentRevisions,
  getCatalogMeta,
  getDefaultPacketIds,
  getDisabledFormIds,
  setDefaultPacketIds,
  setDisabledFormIdsForState,
} from "./forms.js";

function esc(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function $(sel, root = document) {
  return root.querySelector(sel);
}

function stateOptions(selected) {
  return STATES.map(
    (s) =>
      `<option value="${esc(s.code)}" ${s.code === selected ? "selected" : ""}>${esc(
        s.code
      )} — ${esc(s.name)}</option>`
  ).join("");
}

function shell(inner) {
  return `
    <div class="fa-page">
      <div class="fa-wash" aria-hidden="true"></div>
      ${inner}
    </div>
  `;
}

function loginView(error = "") {
  const lockMs = getLoginLockoutMs();
  const locked = lockMs > 0;
  return shell(`
    <main class="fa-card fa-login" role="main">
      <header class="fa-brand">
        <div class="fa-mark" aria-hidden="true">E</div>
        <div>
          <p class="fa-kicker">EnvisionDMS.com</p>
          <h1>Forms Admin</h1>
          <p class="fa-sub">Sign in to assign, unassign, and maintain Envision Forms Library items.</p>
        </div>
      </header>
      <p class="forms-policy-banner">${esc(ENVISION_FORMS_ONLY_POLICY)}</p>
      ${error ? `<p class="fa-error" role="alert">${esc(error)}</p>` : ""}
      ${
        locked
          ? `<p class="fa-error" role="alert">Locked for ${Math.ceil(lockMs / 1000)}s after failed attempts.</p>`
          : ""
      }
      <form id="faLoginForm" class="fa-form" autocomplete="on">
        <label>
          <span>Username</span>
          <input type="text" name="username" id="faUser" value="${esc(DEFAULT_USERNAME)}" autocomplete="username" required ${locked ? "disabled" : ""} />
        </label>
        <label>
          <span>Password</span>
          <input type="password" name="password" id="faPass" autocomplete="current-password" required ${locked ? "disabled" : ""} />
        </label>
        <button type="submit" class="btn btn-primary fa-submit" ${locked ? "disabled" : ""}>Sign in</button>
      </form>
      <p class="fa-fine">Default first-time login: <code>${esc(DEFAULT_USERNAME)}</code> / <code>${esc(DEFAULT_PASSWORD)}</code> — you will be prompted to change the password immediately.</p>
      <p class="fa-fine"><a href="/">← Back to Desking</a></p>
    </main>
  `);
}

function changePasswordView({ forced = false, error = "", success = "" } = {}) {
  const meta = getCredentialsMeta();
  return shell(`
    <main class="fa-card fa-login" role="main">
      <header class="fa-brand">
        <div class="fa-mark" aria-hidden="true">E</div>
        <div>
          <p class="fa-kicker">EnvisionDMS.com</p>
          <h1>${forced ? "Change password required" : "Change password"}</h1>
          <p class="fa-sub">${
            forced
              ? `You are still using the default password (${esc(DEFAULT_PASSWORD)}). Choose a new one before managing forms.`
              : "Update your Forms Admin password anytime."
          }</p>
        </div>
      </header>
      ${error ? `<p class="fa-error" role="alert">${esc(error)}</p>` : ""}
      ${success ? `<p class="fa-success" role="status">${esc(success)}</p>` : ""}
      <form id="faChangeForm" class="fa-form" autocomplete="off">
        <label>
          <span>Current password</span>
          <input type="password" id="faCurrent" autocomplete="current-password" required />
        </label>
        <label>
          <span>New password</span>
          <input type="password" id="faNew" autocomplete="new-password" required minlength="4" />
        </label>
        <label>
          <span>Confirm new password</span>
          <input type="password" id="faConfirm" autocomplete="new-password" required minlength="4" />
        </label>
        <button type="submit" class="btn btn-primary fa-submit">Save new password</button>
      </form>
      <p class="fa-fine">Signed in as <strong>${esc(meta.username)}</strong>${
        meta.updatedAt
          ? ` · last password change ${esc(
              new Date(meta.updatedAt).toLocaleString("en-US", {
                dateStyle: "medium",
                timeStyle: "short",
              })
            )}`
          : ""
      }</p>
      ${
        forced
          ? `<p class="fa-fine"><button type="button" class="linkish" id="faLogout">Sign out</button></p>`
          : `<p class="fa-fine"><button type="button" class="linkish" id="faBackDashboard">← Back to forms</button> · <button type="button" class="linkish" id="faLogout">Sign out</button></p>`
      }
    </main>
  `);
}

let listFilter = "all"; // all | assigned | unassigned
let categoryFilter = "all";
let toastTimer = null;

function showToast(root, message, kind = "ok") {
  let el = root.querySelector("#faToast");
  if (!el) {
    el = document.createElement("div");
    el.id = "faToast";
    el.className = "fa-toast";
    root.querySelector(".fa-page")?.appendChild(el);
  }
  el.className = `fa-toast is-${kind} is-on`;
  el.textContent = message;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove("is-on"), 2800);
}

function formsDashboard(stateCode) {
  const forms = getAllFormsForState(stateCode);
  const disabled = getDisabledFormIds();
  const meta = getCatalogMeta();
  const packet = getDefaultPacketIds(stateCode);
  const revisions = getAssignmentRevisions(12);
  const creds = getCredentialsMeta();
  const assignedCount = forms.filter((f) => !disabled.has(f.id)).length;
  const unassignedCount = forms.length - assignedCount;
  const withOfficial = forms.filter((f) => String(f.formNumber || "").trim()).length;
  const categories = [...new Set(forms.map((f) => f.category || "Other"))].sort((a, b) =>
    a.localeCompare(b)
  );
  const catChips = [
    `<button type="button" class="fa-chip ${categoryFilter === "all" ? "is-on" : ""}" data-cat="all">All categories</button>`,
    ...categories.map(
      (c) =>
        `<button type="button" class="fa-chip ${categoryFilter === c ? "is-on" : ""}" data-cat="${esc(c)}">${esc(c)}</button>`
    ),
  ].join("");

  const rows = forms
    .map((f) => {
      const on = !disabled.has(f.id);
      const num = f.formNumber
        ? `<span class="form-num">${esc(f.formNumber)}</span>`
        : `<span class="form-num muted">Library ID</span>`;
      const hay = `${f.name} ${f.formType} ${f.category} ${f.id} ${f.formNumber || ""} ${f.source || ""}`.toLowerCase();
      const cat = f.category || "Other";
      return `<label class="forms-admin-row ${on ? "" : "is-off"}" data-admin-row data-admin-search="${esc(hay)}" data-assigned="${on ? "1" : "0"}" data-category="${esc(cat)}">
        <input type="checkbox" data-admin-form-id="${esc(f.id)}" ${on ? "checked" : ""} />
        <span class="forms-admin-meta">
          <strong>${esc(f.name)}</strong>
          <small>${num} · ${esc(f.id)} · ${esc(f.formType)} · ${esc(f.source || "ENVISION Forms Library")}</small>
          <span class="fa-row-badges">
            <span class="fa-badge">${esc(cat)}</span>
            ${on ? `<span class="fa-badge fa-badge-on">Assigned</span>` : `<span class="fa-badge fa-badge-off">Unassigned</span>`}
            ${f.formNumber ? `<span class="fa-badge fa-badge-num">Official #</span>` : ""}
          </span>
        </span>
      </label>`;
    })
    .join("");
  const revRows = revisions.length
    ? revisions
        .map(
          (r) =>
            `<li><time>${esc(String(r.at || "").replace("T", " ").slice(0, 19))}</time> · ${esc(r.action || "")}${
              r.stateCode ? ` · ${esc(r.stateCode)}` : ""
            }${r.note ? ` — ${esc(r.note)}` : ""}</li>`
        )
        .join("")
    : `<li class="muted">No local assignment revisions yet.</li>`;
  const gen = meta.generatedAt
    ? new Date(meta.generatedAt).toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";
  const lib = meta.libraryUpdatedAt
    ? new Date(meta.libraryUpdatedAt).toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

  return shell(`
    <div class="fa-dashboard">
      <header class="fa-dash-head">
        <div class="fa-brand fa-brand-inline">
          <div class="fa-mark" aria-hidden="true">E</div>
          <div>
            <p class="fa-kicker">EnvisionDMS.com</p>
            <h1>Forms Admin</h1>
            <p class="fa-sub">Library control · assign / unassign · revisions</p>
          </div>
        </div>
        <div class="fa-dash-actions">
          <label class="fa-state">
            <span>State</span>
            <select id="faState">${stateOptions(stateCode)}</select>
          </label>
          <button type="button" class="btn btn-ghost" id="faCopyLink" title="Copy Forms Admin URL">Copy link</button>
          <button type="button" class="btn btn-ghost" id="faChangePw">Change password</button>
          <button type="button" class="btn btn-ghost" id="faLogout">Sign out</button>
          <a class="btn btn-ghost" href="/">Desking</a>
        </div>
      </header>

      <p class="forms-policy-banner">${esc(ENVISION_FORMS_ONLY_POLICY)}</p>

      <div class="fa-stats">
        <div class="fa-stat"><span>Assigned</span><strong id="faStatAssigned">${assignedCount}</strong></div>
        <div class="fa-stat"><span>Unassigned</span><strong id="faStatUnassigned">${unassignedCount}</strong></div>
        <div class="fa-stat"><span>In library</span><strong>${forms.length}</strong></div>
        <div class="fa-stat"><span>Official #</span><strong>${withOfficial}</strong></div>
        <div class="fa-stat"><span>Default packet</span><strong>${packet?.length ? packet.length : "Priority"}</strong></div>
      </div>

      <dl class="forms-admin-meta-grid">
        <div><dt>Signed in</dt><dd>${esc(creds.username)}</dd></div>
        <div><dt>Catalog rev</dt><dd>${esc(String(meta.revision || meta.version || "—"))}</dd></div>
        <div><dt>Catalog built</dt><dd>${esc(gen)}</dd></div>
        <div><dt>Library stamp</dt><dd>${esc(lib)}</dd></div>
        <div><dt>Assigned here</dt><dd id="adminAssignedCount">${assignedCount} / ${forms.length}</dd></div>
      </dl>

      <details class="fa-help">
        <summary>How to update &amp; assign forms</summary>
        <ol>
          <li><strong>Porter DMS repo</strong> — Edit master forms in <strong>Porter DMS → Forms Module</strong> (Envision library only). Save/publish there so <code>live.json</code> updates.</li>
          <li><strong>This repo (Public Desking)</strong> — From this project folder run <code>npm run catalog</code>, commit the updated <code>src/data/forms-catalog.json</code>, then deploy <strong>this</strong> app (Railway / <code>public.envisiondms.com</code>). Do <em>not</em> run catalog or deploy from the Porter DMS repo for Public Desking.</li>
          <li><strong>Here in Forms Admin</strong> — Check = assign for Start Deal, uncheck = unassign, then <strong>Save Assignments</strong>.</li>
          <li>Optional: <strong>Save Default Packet</strong> sets this state’s default Start Deal selection.</li>
          <li>Change your password anytime with <strong>Change password</strong>.</li>
        </ol>
      </details>

      <div class="fa-filter-bar">
        <div class="fa-tabs" role="tablist" aria-label="Assignment filter">
          <button type="button" class="fa-tab ${listFilter === "all" ? "is-on" : ""}" data-filter="all">All</button>
          <button type="button" class="fa-tab ${listFilter === "assigned" ? "is-on" : ""}" data-filter="assigned">Assigned</button>
          <button type="button" class="fa-tab ${listFilter === "unassigned" ? "is-on" : ""}" data-filter="unassigned">Unassigned</button>
        </div>
        <div class="forms-admin-toolbar">
          <input type="search" id="adminFormsSearch" placeholder="Search name, ID, type, form #" aria-label="Search forms" />
          <span id="adminFilterMeta" class="forms-admin-filter-meta">${forms.length} forms</span>
        </div>
      </div>
      <div class="fa-chips" id="faCatChips">${catChips}</div>

      <div class="forms-admin-list fa-list">${rows || `<p class="muted">No Envision library forms for ${esc(stateCode)}.</p>`}</div>

      <div class="fa-sticky-bar">
        <div class="fa-sticky-meta"><span id="faDirtyHint">Edit checks, then save</span></div>
        <div class="fa-toolbar-row">
          <button type="button" class="btn btn-ghost" id="btnAdminEnableAll">Assign All</button>
          <button type="button" class="btn btn-ghost" id="btnAdminDisableAll">Unassign All</button>
          <button type="button" class="btn btn-ghost" id="btnAdminPriorityOnly">Priority Only</button>
          <button type="button" class="btn btn-ghost" id="btnAdminVisible">Assign Visible</button>
          <button type="button" class="btn btn-ghost" id="btnAdminSavePacket">Save Default Packet</button>
          <button type="button" class="btn btn-ghost" id="btnAdminClearPacket">Clear Default Packet</button>
          <button type="button" class="btn btn-ghost" id="btnAdminExport">Export JSON</button>
          <button type="button" class="btn btn-primary" id="btnAdminSave">Save Assignments</button>
        </div>
      </div>

      <details class="forms-admin-revisions" open>
        <summary>Assignment revision log (this browser)</summary>
        <ol>${revRows}</ol>
      </details>
      <div id="faToast" class="fa-toast" role="status" aria-live="polite"></div>
    </div>
  `);
}

let viewMode = "auto"; // auto | changePassword
let selectedState =
  localStorage.getItem("envision.formsAdmin.stateCode") || "FL";

function setView(mode) {
  viewMode = mode;
  renderFormsAdmin(document.getElementById("app"));
}

function wireLogin(root) {
  $("#faLoginForm", root)?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const username = $("#faUser", root)?.value || "";
    const password = $("#faPass", root)?.value || "";
    const result = await login(username, password);
    if (!result.ok) {
      root.innerHTML = loginView(result.error);
      wireLogin(root);
      return;
    }
    if (result.mustChangePassword) {
      setView("changePassword");
      return;
    }
    setView("dashboard");
  });
}

function wireChangePassword(root, forced) {
  $("#faChangeForm", root)?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const result = await changePassword(
      $("#faCurrent", root)?.value,
      $("#faNew", root)?.value,
      $("#faConfirm", root)?.value
    );
    if (!result.ok) {
      root.innerHTML = changePasswordView({ forced, error: result.error });
      wireChangePassword(root, forced);
      return;
    }
    root.innerHTML = changePasswordView({
      forced: false,
      success: "Password updated. You can manage forms now.",
    });
    wireChangePassword(root, false);
    if (forced) {
      setTimeout(() => setView("dashboard"), 600);
    }
  });
  $("#faLogout", root)?.addEventListener("click", () => {
    logout();
    setView("login");
  });
  $("#faBackDashboard", root)?.addEventListener("click", () => setView("dashboard"));
}

function wireDashboard(root, stateCode) {
  let dirty = false;
  const markDirty = () => {
    dirty = true;
    const hint = $("#faDirtyHint", root);
    if (hint) hint.textContent = "Unsaved changes";
  };

  $("#faState", root)?.addEventListener("change", (e) => {
    if (dirty && !confirm("You have unsaved assignment changes. Switch state anyway?")) {
      e.target.value = selectedState;
      return;
    }
    selectedState = e.target.value;
    localStorage.setItem("envision.formsAdmin.stateCode", selectedState);
    listFilter = "all";
    categoryFilter = "all";
    setView("dashboard");
  });
  $("#faChangePw", root)?.addEventListener("click", () => setView("changePassword"));
  $("#faLogout", root)?.addEventListener("click", () => {
    logout();
    setView("login");
  });
  $("#faCopyLink", root)?.addEventListener("click", async () => {
    const url = `${location.origin}/formsadmin`;
    try {
      await navigator.clipboard.writeText(url);
      showToast(root, "Forms Admin link copied", "ok");
    } catch {
      prompt("Copy this Forms Admin URL:", url);
    }
  });

  const refreshCount = () => {
    const total = root.querySelectorAll("[data-admin-form-id]").length;
    const on = root.querySelectorAll("[data-admin-form-id]:checked").length;
    const el = $("#adminAssignedCount", root);
    if (el) el.textContent = `${on} / ${total}`;
    const a = $("#faStatAssigned", root);
    const u = $("#faStatUnassigned", root);
    if (a) a.textContent = String(on);
    if (u) u.textContent = String(total - on);
    root.querySelectorAll("[data-admin-row]").forEach((row) => {
      const input = row.querySelector("[data-admin-form-id]");
      const assigned = !!input?.checked;
      row.classList.toggle("is-off", !assigned);
      row.setAttribute("data-assigned", assigned ? "1" : "0");
      const badge = row.querySelector(".fa-badge-on, .fa-badge-off");
      if (badge) {
        badge.className = assigned ? "fa-badge fa-badge-on" : "fa-badge fa-badge-off";
        badge.textContent = assigned ? "Assigned" : "Unassigned";
      }
    });
  };

  const applyFilters = () => {
    const q = String($("#adminFormsSearch", root)?.value || "")
      .trim()
      .toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    let visible = 0;
    root.querySelectorAll("[data-admin-row]").forEach((row) => {
      const hay = row.getAttribute("data-admin-search") || "";
      const assigned = row.getAttribute("data-assigned") === "1";
      const cat = row.getAttribute("data-category") || "";
      const matchText = !tokens.length || tokens.every((t) => hay.includes(t));
      const matchList =
        listFilter === "all" ||
        (listFilter === "assigned" && assigned) ||
        (listFilter === "unassigned" && !assigned);
      const matchCat = categoryFilter === "all" || cat === categoryFilter;
      const show = matchText && matchList && matchCat;
      row.hidden = !show;
      if (show) visible += 1;
    });
    const metaEl = $("#adminFilterMeta", root);
    if (metaEl) {
      metaEl.textContent = `${visible} shown`;
    }
  };

  root.querySelectorAll("[data-filter]").forEach((btn) => {
    btn.addEventListener("click", () => {
      listFilter = btn.getAttribute("data-filter") || "all";
      root.querySelectorAll("[data-filter]").forEach((b) =>
        b.classList.toggle("is-on", b === btn)
      );
      applyFilters();
    });
  });
  root.querySelectorAll("[data-cat]").forEach((btn) => {
    btn.addEventListener("click", () => {
      categoryFilter = btn.getAttribute("data-cat") || "all";
      root.querySelectorAll("[data-cat]").forEach((b) =>
        b.classList.toggle("is-on", b === btn)
      );
      applyFilters();
    });
  });

  $("#adminFormsSearch", root)?.addEventListener("input", applyFilters);
  root.querySelectorAll("[data-admin-form-id]").forEach((input) => {
    input.addEventListener("change", () => {
      markDirty();
      refreshCount();
      applyFilters();
    });
  });
  applyFilters();

  $("#btnAdminEnableAll", root)?.addEventListener("click", () => {
    root.querySelectorAll("[data-admin-form-id]").forEach((input) => {
      input.checked = true;
    });
    markDirty();
    refreshCount();
    applyFilters();
  });
  $("#btnAdminDisableAll", root)?.addEventListener("click", () => {
    if (!confirm("Unassign all forms for this browser?")) return;
    root.querySelectorAll("[data-admin-form-id]").forEach((input) => {
      input.checked = false;
    });
    markDirty();
    refreshCount();
    applyFilters();
  });
  $("#btnAdminPriorityOnly", root)?.addEventListener("click", () => {
    const priority = new Set(getCatalogMeta().priorityTypes || []);
    const byId = new Map(getAllFormsForState(stateCode).map((f) => [f.id, f]));
    root.querySelectorAll("[data-admin-form-id]").forEach((input) => {
      const form = byId.get(input.getAttribute("data-admin-form-id"));
      input.checked = !!(form && priority.has(form.formType));
    });
    markDirty();
    refreshCount();
    applyFilters();
  });
  $("#btnAdminVisible", root)?.addEventListener("click", () => {
    root.querySelectorAll("[data-admin-row]:not([hidden]) [data-admin-form-id]").forEach((input) => {
      input.checked = true;
    });
    markDirty();
    refreshCount();
    applyFilters();
  });
  $("#btnAdminSave", root)?.addEventListener("click", () => {
    const disabled = [...root.querySelectorAll("[data-admin-form-id]")]
      .filter((el) => !el.checked)
      .map((el) => el.getAttribute("data-admin-form-id"));
    const assigned = root.querySelectorAll("[data-admin-form-id]:checked").length;
    setDisabledFormIdsForState(stateCode, disabled, {
      action: "save-assignments",
      note: `Saved ${assigned} assigned form(s) for ${stateCode}`,
    });
    dirty = false;
    showToast(root, `Saved assignments for ${stateCode} (${assigned} assigned)`, "ok");
    setTimeout(() => setView("dashboard"), 400);
  });
  $("#btnAdminSavePacket", root)?.addEventListener("click", () => {
    const ids = [...root.querySelectorAll("[data-admin-form-id]:checked")].map(
      (el) => el.getAttribute("data-admin-form-id")
    );
    if (!ids.length) {
      showToast(root, "Select at least one assigned form for the default packet", "err");
      return;
    }
    // Persist assignment checks first so remount cannot wipe unsaved assign/unassign work.
    if (dirty) {
      const disabled = [...root.querySelectorAll("[data-admin-form-id]")]
        .filter((el) => !el.checked)
        .map((el) => el.getAttribute("data-admin-form-id"));
      setDisabledFormIdsForState(stateCode, disabled, {
        action: "save-assignments",
        note: `Auto-saved assignments with default packet for ${stateCode}`,
      });
      dirty = false;
    }
    setDefaultPacketIds(stateCode, ids);
    showToast(root, `Default packet saved (${ids.length} forms)`, "ok");
    setTimeout(() => setView("dashboard"), 400);
  });
  $("#btnAdminClearPacket", root)?.addEventListener("click", () => {
    if (dirty) {
      const ok = window.confirm(
        "You have unsaved assignment changes. Clear the default packet and stay on this screen? (Assignments are not saved yet.)"
      );
      if (!ok) return;
    }
    clearDefaultPacketIds(stateCode);
    showToast(root, `Cleared default packet for ${stateCode}`, "ok");
    // Stay on assign view so unsaved checkbox edits are not wiped by a remount.
  });
  $("#btnAdminExport", root)?.addEventListener("click", () => {
    const snap = exportFormsAssignmentSnapshot(stateCode);
    const blob = new Blob([JSON.stringify(snap, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `envision-forms-assignment-${stateCode}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(root, "Assignment JSON exported", "ok");
  });
}

export async function renderFormsAdmin(root) {
  if (!root) return;
  document.title = "Forms Admin · EnvisionDMS.com";
  await ensureDefaultCredentials();

  const authed = isAuthenticated();
  const forced = authed && mustChangePassword();

  if (!authed || viewMode === "login") {
    viewMode = "login";
    root.innerHTML = loginView();
    wireLogin(root);
    return;
  }

  if (forced || viewMode === "changePassword") {
    const force = forced || mustChangePassword();
    root.innerHTML = changePasswordView({ forced: force });
    wireChangePassword(root, force);
    return;
  }

  viewMode = "dashboard";
  root.innerHTML = formsDashboard(selectedState);
  wireDashboard(root, selectedState);
}

export function mountFormsAdmin(root) {
  viewMode = "auto";
  return renderFormsAdmin(root);
}
